import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import {
  formatAdminTableDate,
  type AdminTableDateOptions,
} from "../../../lib/formatAdminTableDate";
import type {
  AdminOrganizationOverviewRow,
  AdminRouteMap,
  AdminUnassignedUserRow,
} from "../types";

type AdminOrganizationsPageProps = {
  organizations: AdminOrganizationOverviewRow[];
  unassignedUsers: AdminUnassignedUserRow[];
  dateOptions: AdminTableDateOptions;
  adminRoutes: AdminRouteMap;
};

const orgRowHref = (adminRoutes: AdminRouteMap, id: number) =>
  adminRoutes.adminOrganization.replace(":id", String(id));

const userRowHref = (adminRoutes: AdminRouteMap, id: number) =>
  adminRoutes.adminUser.replace(":id", String(id));

export const AdminOrganizationsPage: React.FC<AdminOrganizationsPageProps> = ({
  organizations,
  unassignedUsers,
  dateOptions,
  adminRoutes,
}) => (
  <DashboardPageLayout
    title="Organizations"
    subtitle="Company accounts, billing, and setup progress (last 200). Click a row for members, quotes, and chats."
  >
    <Card>
      <CardHeader>
        <CardTitle>All organizations</CardTitle>
        <CardDescription>
          Suite billing reflects product entitlements even before a public
          website exists.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 font-medium">Company</th>
                <th className="text-left py-3 px-4 font-medium">Owner</th>
                <th className="text-left py-3 px-4 font-medium">Created</th>
                <th className="text-left py-3 px-4 font-medium">Trial ends</th>
                <th className="text-left py-3 px-4 font-medium">Website</th>
                <th className="text-left py-3 px-4 font-medium">Billing</th>
                <th className="text-right py-3 px-4 font-medium">Members</th>
                <th className="text-right py-3 px-4 font-medium">Jobs</th>
                <th className="text-right py-3 px-4 font-medium">Quotes</th>
                <th className="text-right py-3 px-4 font-medium">Chats</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((row) => {
                const href = orgRowHref(adminRoutes, row.id);
                const go = () => {
                  window.location.assign(href);
                };
                const label = row.name || row.ownerEmail || `Org #${row.id}`;
                return (
                  <tr
                    key={row.id}
                    className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    tabIndex={0}
                    role="link"
                    aria-label={`View organization ${label}`}
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        go();
                      }
                    }}
                  >
                    <td className="py-3 px-4 font-medium">{row.name}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5">
                        {row.ownerName ? (
                          <span>{row.ownerName}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                        {row.ownerEmail ? (
                          <span className="text-xs text-muted-foreground">
                            {row.ownerEmail}
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {formatAdminTableDate(row.createdAt, dateOptions)}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">
                      {row.trialEndsAt
                        ? formatAdminTableDate(row.trialEndsAt, dateOptions)
                        : "—"}
                    </td>
                    <td className="py-3 px-4 max-w-48">
                      {row.business ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium truncate block">
                            {row.business.businessName ??
                              row.business.subdomain}
                          </span>
                          <span className="text-xs text-muted-foreground truncate">
                            {row.business.subdomain}
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">Not yet</span>
                      )}
                    </td>
                    <td className="py-3 px-4 capitalize">
                      {row.suiteBillingStatus.replace(/_/g, " ")}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.memberCount}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.jobSiteCount}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.quoteCount}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums">
                      {row.chatSessionCount}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {unassignedUsers.length > 0 ? (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Users without an organization</CardTitle>
          <CardDescription>
            Signed up but have not joined or created a company yet (
            {unassignedUsers.length} shown).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-left py-3 px-4 font-medium">Name</th>
                  <th className="text-left py-3 px-4 font-medium">Created</th>
                  <th className="text-left py-3 px-4 font-medium">
                    User trial ends
                  </th>
                  <th className="text-left py-3 px-4 font-medium">Admin</th>
                </tr>
              </thead>
              <tbody>
                {unassignedUsers.map((row) => {
                  const href = userRowHref(adminRoutes, row.id);
                  const go = () => {
                    window.location.assign(href);
                  };
                  return (
                    <tr
                      key={row.id}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50"
                      tabIndex={0}
                      role="link"
                      aria-label={`View user ${row.email}`}
                      onClick={go}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          go();
                        }
                      }}
                    >
                      <td className="py-3 px-4">{row.email}</td>
                      <td className="py-3 px-4">
                        {row.name ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {formatAdminTableDate(row.createdAt, dateOptions)}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {row.trialEndsAt
                          ? formatAdminTableDate(row.trialEndsAt, dateOptions)
                          : "—"}
                      </td>
                      <td className="py-3 px-4">{row.admin ? "Yes" : "No"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    ) : null}
  </DashboardPageLayout>
);
